import React, { useEffect, useRef, useState } from 'react';

/** Stop polling for inner video after this many attempts (~5s) to avoid indefinite loop */
const MAX_VIDEO_RESOLVE_ATTEMPTS = 50;

/** Target FPS for the blur preview (lower = less CPU; 24 is enough for background blur) */
const TARGET_FPS = 24;
const FRAME_INTERVAL_MS = 1000 / TARGET_FPS;

/**
 * Internal canvas size: container / CANVAS_SCALE_DIVISOR, capped by MAX_CANVAS_DIMENSION.
 * With low-res video source we use a smaller canvas (larger divisor, lower max) = fewer pixels
 * to draw and faster performance; the blur hides blockiness when upscaled.
 */
const CANVAS_SCALE_DIVISOR = 6;
const MAX_CANVAS_DIMENSION = 96;

/**
 * CSS blur on the upscaled canvas. Kept modest (10px) because we already use a low-res
 * canvas and low-res video source—smaller blur = less GPU work and faster performance.
 */
const BLUR_FILTER = 'blur(10px)';

/** Custom element that wraps a video (e.g. hls-video uses .nativeEl or .video) */
type VideoHostElement = HTMLElement & {
  nativeEl?: HTMLVideoElement;
  video?: HTMLVideoElement;
  shadowRoot?: ShadowRoot | null;
};

/** Ref may be a plain <video> or a custom element (e.g. <hls-video>) that wraps one */
type BlurredVideoOverlayProps = {
  videoElement: HTMLVideoElement | HTMLElement | null;
  enabled: boolean;
};

/**
 * Single full-size canvas behind the video: draw the video scaled to a small
 * internal resolution, then CSS upscale + blur for a cheap blurred preview.
 */
export const BlurredVideoOverlay = ({
  videoElement,
  enabled,
}: BlurredVideoOverlayProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastDrawTimeRef = useRef<number>(0);
  const containerSizeRef = useRef({ width: 0, height: 0 });
  const [actualVideoElement, setActualVideoElement] =
    useState<HTMLVideoElement | null>(null);

  // Resolve the real <video> used for playback. Ref may be a plain <video> or a
  // custom element (e.g. hls-video) that exposes it via .nativeEl or shadow DOM.
  useEffect(() => {
    if (!videoElement) {
      setActualVideoElement(null);
      return;
    }

    if (videoElement.tagName === 'VIDEO') {
      setActualVideoElement(videoElement as HTMLVideoElement);
      return;
    }

    const host = videoElement as VideoHostElement;

    const resolve = (attempt: number): (() => void) | void => {
      if (host.nativeEl) {
        setActualVideoElement(host.nativeEl);
        return;
      }
      if (host.video) {
        setActualVideoElement(host.video);
        return;
      }
      const video = host.shadowRoot?.querySelector('video');
      if (video) {
        setActualVideoElement(video);
        return;
      }
      if (attempt >= MAX_VIDEO_RESOLVE_ATTEMPTS) {
        return;
      }
      const timeoutId = setTimeout(() => resolve(attempt + 1), 100);
      return () => clearTimeout(timeoutId);
    };

    return resolve(0);
  }, [videoElement]);

  useEffect(() => {
    if (!enabled || !actualVideoElement || !containerRef.current) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    const canvas = canvasRef.current;
    const container = containerRef.current;
    const video = actualVideoElement;

    if (!canvas) return;

    const ctx = canvas.getContext('2d', {
      willReadFrequently: false,
    });
    if (!ctx) return;

    // Set once: blur, smooth upscaling, full-size display
    canvas.style.filter = BLUR_FILTER;
    canvas.style.imageRendering = 'auto';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.objectFit = 'cover';

    const updateCanvasSize = () => {
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      containerSizeRef.current = {
        width: containerWidth,
        height: containerHeight,
      };

      const { videoWidth, videoHeight } = video;
      if (videoWidth === 0 || videoHeight === 0) return;

      // Small internal resolution; cap to MAX_CANVAS_DIMENSION to reduce pixels
      let drawW = Math.round(containerWidth / CANVAS_SCALE_DIVISOR);
      let drawH = Math.round(containerHeight / CANVAS_SCALE_DIVISOR);
      const max = MAX_CANVAS_DIMENSION;
      if (drawW > max || drawH > max) {
        const s = max / Math.max(drawW, drawH);
        drawW = Math.round(drawW * s);
        drawH = Math.round(drawH * s);
      }
      drawW = Math.max(1, drawW);
      drawH = Math.max(1, drawH);

      canvas.width = drawW;
      canvas.height = drawH;
    };

    updateCanvasSize();

    const resizeObserver = new ResizeObserver(() => {
      updateCanvasSize();
    });
    resizeObserver.observe(container);

    const drawFrame = () => {
      const videoWidth = video?.videoWidth ?? 0;
      const videoHeight = video?.videoHeight ?? 0;
      let { width: containerWidth, height: containerHeight } =
        containerSizeRef.current;
      if (containerHeight === 0 && container?.offsetHeight) {
        containerSizeRef.current = {
          width: container.offsetWidth,
          height: container.offsetHeight,
        };
        containerHeight = containerSizeRef.current.height;
        containerWidth = containerSizeRef.current.width;
      }
      const canDraw =
        video &&
        video.readyState >= 2 &&
        videoWidth > 0 &&
        videoHeight > 0 &&
        containerWidth > 0 &&
        containerHeight > 0;
      if (!canDraw) {
        animationFrameRef.current = requestAnimationFrame(drawFrame);
        return;
      }

      // Recompute canvas size when video just got dimensions
      if (canvas.width === 0 || canvas.height === 0) {
        updateCanvasSize();
      }

      // Throttle to ~TARGET_FPS
      const now = performance.now();
      if (now - lastDrawTimeRef.current >= FRAME_INTERVAL_MS) {
        lastDrawTimeRef.current = now;
        if (canvas.width > 0 && canvas.height > 0) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(
            video,
            0,
            0,
            videoWidth,
            videoHeight,
            0,
            0,
            canvas.width,
            canvas.height,
          );
        }
      }

      animationFrameRef.current = requestAnimationFrame(drawFrame);
    };

    animationFrameRef.current = requestAnimationFrame(drawFrame);

    return () => {
      resizeObserver.disconnect();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [enabled, actualVideoElement]);

  if (!enabled) return null;

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 z-0 pointer-events-none"
      aria-hidden="true"
    >
      <canvas ref={canvasRef} className="absolute inset-0" />
    </div>
  );
};
